import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus } from "lucide-react";
import { apiErrorMessage } from "@/lib/api";
import { useGeneratePostImage } from "@/lib/content";
import { Spinner } from "@/components/ui";

// =============================================================================
// "This post has no picture. Give it one."
//
// Posts arrive without images for ordinary reasons — the image call failed, the
// prompt tripped a content filter, the brand had AI images off at the time, or
// a thirty-post month hit a rate limit halfway through. The captions are fine
// and may already have been approved, and on Instagram an image is not
// optional: it IS the post.
//
// Deliberately NOT the regenerate button. Regenerating rewrites the captions,
// which is the wrong tool when only the picture is missing, and the label says
// so in as many words — "Generate image" next to "keeps your caption".
//
// Owns its own mutation rather than taking one as a prop, so clicking one post
// does not disable the button on every other post in the list.
// =============================================================================

export function GenerateImageButton({
  postId,
  /** `inline` for the calendar's one-line rows, `block` for the approvals card. */
  variant = "inline",
}: {
  postId: string;
  variant?: "inline" | "block";
}) {
  const { t } = useTranslation();
  const generate = useGeneratePostImage();
  const [error, setError] = useState<string | null>(null);

  function run() {
    setError(null);
    generate.mutate(postId, {
      onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
    });
  }

  const busy = generate.isPending;

  return (
    <div className={variant === "block" ? "mt-2 space-y-1" : "mt-3 space-y-1"}>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        className={
          variant === "block"
            ? "btn-secondary w-full justify-center text-xs"
            : "inline-flex items-center gap-1.5 text-xs text-primary-700 hover:underline disabled:opacity-50"
        }
      >
        {busy ? <Spinner /> : <ImagePlus size={13} />}
        {busy ? t("calendar.generatingImage") : t("calendar.generateImage")}
      </button>
      {/* Said once, next to the button, because "generate" next to a post that
          already has approved words reads like it might replace them. */}
      {!busy && !error ? (
        <p className="text-[10px] leading-snug text-muted">{t("calendar.keepsCaption")}</p>
      ) : null}
      {busy ? (
        <p className="text-[10px] leading-snug text-muted">{t("calendar.imageTakesAMoment")}</p>
      ) : null}
      {error ? <p className="text-[10px] leading-snug text-danger">{error}</p> : null}
    </div>
  );
}
